import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, User, Calendar, MapPin, CreditCard, FileText,
    Download, FileSpreadsheet, FileJson, Loader2, Star,
    Phone, Mail, CheckCircle, XCircle, Clock, Filter,
    Wallet, Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import DataTablePagination from '@/components/ui/data-table-pagination';

import { useRiderProfile, useRiderTripHistory, useExportRiderData } from '@/api-services/hooks/useUserProfile';
import { TripStatus, ExportFormat, ExportDataOptions } from '@/api-services/hooks/useUserProfile';

const RiderProfilePage = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const riderId = parseInt(id || '0');

    // Local state
    const [activeTab, setActiveTab] = useState('profile');
    const [tripStatusFilter, setTripStatusFilter] = useState<TripStatus | 'all'>('all');
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [exportFormat, setExportFormat] = useState<ExportFormat>('CSV');
    const [includeProfile, setIncludeProfile] = useState(true);
    const [includeTrips, setIncludeTrips] = useState(true);
    const [includeStats, setIncludeStats] = useState(true);

    // API Hooks
    const { data: rider, loading: isLoadingRider, refetch: refetchRider } = useRiderProfile(riderId, {
        enabled: !!riderId,
    });

    const { data: tripHistory, isLoading: isLoadingTrips } = useRiderTripHistory(
        riderId,
        {
            page: currentPage,
            size: itemsPerPage,
            status: tripStatusFilter === 'all' ? undefined : tripStatusFilter,
        },
        { enabled: !!riderId && activeTab === 'trips' }
    );

    const { mutate: exportRiderData, isPending: isExporting } = useExportRiderData({
        onSuccess: () => {
            toast.success('Rider data exported successfully');
        },
        onError: (error) => {
            toast.error(`Export failed: ${error.message}`);
        },
    });

    // Reset pagination when filter changes
    useEffect(() => {
        setCurrentPage(0);
    }, [tripStatusFilter]);

    // Handle export
    const handleExport = () => {
        const options: ExportDataOptions = {
            format: exportFormat,
            includeProfile,
            includeTrips,
            includeStats,
        };
        exportRiderData({ riderId, options });
    };

    // Get status badge for trips
    const getTripStatusBadge = (status: TripStatus) => {
        const config: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; className: string }> = {
            PENDING: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800 border-yellow-300' },
            ACCEPTED: { variant: 'outline', className: 'bg-blue-100 text-blue-800 border-blue-300' },
            IN_PROGRESS: { variant: 'default', className: 'bg-green-500 text-white' },
            COMPLETED: { variant: 'default', className: 'bg-emerald-600 text-white' },
            CANCELLED: { variant: 'destructive', className: '' },
            FAILED: { variant: 'secondary', className: 'bg-gray-500 text-white' },
        };
        const badgeConfig = config[status] || { variant: 'outline', className: '' };
        return <Badge variant={badgeConfig.variant} className={badgeConfig.className}>{status.replace('_', ' ')}</Badge>;
    };

    // Format currency
    const formatCurrency = (amount?: number) => {
        if (amount === undefined || amount === null) return 'N/A';
        return `$${Number(amount).toFixed(2)}`;
    };

    // Format date
    const formatDate = (dateString?: string) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    // Get initials for avatar
    const getInitials = (name?: string) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    // Loading state
    if (isLoadingRider) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    // Not found state
    if (!rider) {
        return (
            <div className="p-8 text-center">
                <h2 className="text-2xl font-bold text-destructive">Rider not found</h2>
                <Button onClick={() => navigate('/admin/riders')} className="mt-4">
                    Back to Riders
                </Button>
            </div>
        );
    }

    const trips = tripHistory?.content || [];
    const totalTrips = tripHistory?.page?.totalElements || 0;
    const totalPages = tripHistory?.page?.totalPages || 1;

    return (
        <div className="space-y-6 animate-fade-in pb-10">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center space-x-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/admin/riders')}
                        className="hover:bg-muted"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                    <div className="flex items-center gap-3">
                        <Avatar className="h-14 w-14 border-2 border-primary/10">
                            <AvatarImage src={rider.profilePhotoUrl || ''} alt={rider.fullName} />
                            <AvatarFallback className="bg-primary/10 text-primary text-lg">
                                {getInitials(rider.fullName)}
                            </AvatarFallback>
                        </Avatar>
                        <div>
                            <h1 className="text-2xl font-bold text-foreground">{rider.fullName || 'Unknown'}</h1>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                <span>{rider.phoneNumber || 'N/A'}</span>
                                <span>•</span>
                                <Mail className="w-3 h-3" />
                                <span>{rider.emailId || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1.5 rounded-full border border-yellow-200">
                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        <span className="font-medium">{rider.riderRating || rider.averageRating || 'N/A'}</span>
                    </div>
                    <Badge variant={rider.isActive ? 'default' : 'secondary'}>
                        {rider.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid w-full grid-cols-3 h-auto">
                    <TabsTrigger value="profile" className="py-2">
                        <User className="w-4 h-4 mr-2" />
                        Profile Info
                    </TabsTrigger>
                    <TabsTrigger value="trips" className="py-2">
                        <MapPin className="w-4 h-4 mr-2" />
                        Trip History
                    </TabsTrigger>
                    <TabsTrigger value="export" className="py-2">
                        <Download className="w-4 h-4 mr-2" />
                        Export Data
                    </TabsTrigger>
                </TabsList>

                {/* Profile Tab */}
                <TabsContent value="profile" className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Personal Details */}
                        <Card className="lg:col-span-2">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <User className="w-5 h-5 text-primary" />
                                    Personal Details
                                </CardTitle>
                                <CardDescription>Rider's personal information</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Full Name</Label>
                                        <p className="font-medium">{rider.fullName || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Phone Number</Label>
                                        <p className="font-medium">{rider.phoneNumber || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Email</Label>
                                        <p className="font-medium">{rider.emailId || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Date of Birth</Label>
                                        <p className="font-medium">{formatDate(rider.dateOfBirth)}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Gender</Label>
                                        <p className="font-medium">{rider.gender || 'N/A'}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-muted-foreground">Preferred Language</Label>
                                        <p className="font-medium">{rider.preferredLanguage || 'N/A'}</p>
                                    </div>
                                </div>
                                <Separator />
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Home Address</Label>
                                    <p className="font-medium">{rider.homeAddress || 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Work Address</Label>
                                    <p className="font-medium">{rider.workAddress || 'N/A'}</p>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Stats Card */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Star className="w-5 h-5 text-primary" />
                                    Statistics
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Total Trips</span>
                                    <span className="font-bold text-lg">{rider.totalTrips || 0}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Completed</span>
                                    <span className="font-bold text-lg text-green-600">{rider.completedTrips || 0}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Cancelled</span>
                                    <span className="font-bold text-lg text-red-600">{rider.cancelledTrips || 0}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Total Spent</span>
                                    <span className="font-bold text-lg">{formatGlobalCurrency(rider.totalSpent)}</span>
                                </div>
                                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                                    <span className="text-muted-foreground">Member Since</span>
                                    <span className="font-medium">{formatDate(rider.memberSince)}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* KYC Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="w-5 h-5 text-primary" />
                                KYC Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">ID Type</Label>
                                    <p className="font-medium">{rider.kycIdType || 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">ID Number</Label>
                                    <p className="font-medium">{rider.kycIdNumber || 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">OTP Verified</Label>
                                    <div className="flex items-center gap-2">
                                        {rider.otpVerified ? (
                                            <>
                                                <CheckCircle className="w-4 h-4 text-green-500" />
                                                <span className="font-medium text-green-600">Verified</span>
                                            </>
                                        ) : (
                                            <>
                                                <XCircle className="w-4 h-4 text-red-500" />
                                                <span className="font-medium text-red-600">Not Verified</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {rider.kycDocumentPhotoUrl && (
                                <div className="mt-4">
                                    <Label className="text-muted-foreground">KYC Document</Label>
                                    <div className="mt-2">
                                        <a
                                            href={rider.kycDocumentPhotoUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline"
                                        >
                                            View Document
                                        </a>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Payment Methods */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <CreditCard className="w-5 h-5 text-primary" />
                                Payment Information
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Preferred Payment</Label>
                                    <p className="font-medium">{rider.preferredPaymentMethod || 'N/A'}</p>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Wallet Balance</Label>
                                    <div className="flex items-center gap-2">
                                        <Wallet className="w-4 h-4 text-primary" />
                                        <span className="font-medium">{formatGlobalCurrency(rider.walletBalance)}</span>
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-muted-foreground">Signup Method</Label>
                                    <p className="font-medium">{rider.signupMethod || 'N/A'}</p>
                                </div>
                            </div>
                            {rider.savedPaymentMethods && rider.savedPaymentMethods.length > 0 && (
                                <div className="mt-4">
                                    <Label className="text-muted-foreground">Saved Payment Methods</Label>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {rider.savedPaymentMethods.map((method, index) => (
                                            <Badge key={index} variant="outline">{method}</Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Trip History Tab */}
                <TabsContent value="trips">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <MapPin className="w-5 h-5 text-primary" />
                                        Trip History
                                    </CardTitle>
                                    <CardDescription>All trips taken by this rider</CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Filter className="w-4 h-4 text-muted-foreground" />
                                    <Select value={tripStatusFilter} onValueChange={(value) => setTripStatusFilter(value as TripStatus | 'all')}>
                                        <SelectTrigger className="w-[160px]">
                                            <SelectValue placeholder="Filter by status" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all">All Statuses</SelectItem>
                                            <SelectItem value="PENDING">Pending</SelectItem>
                                            <SelectItem value="ACCEPTED">Accepted</SelectItem>
                                            <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                                            <SelectItem value="COMPLETED">Completed</SelectItem>
                                            <SelectItem value="CANCELLED">Cancelled</SelectItem>
                                            <SelectItem value="FAILED">Failed</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Trip ID</TableHead>
                                            <TableHead>Date</TableHead>
                                            <TableHead>Pickup</TableHead>
                                            <TableHead>Dropoff</TableHead>
                                            <TableHead>Driver</TableHead>
                                            <TableHead>Fare</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {isLoadingTrips ? (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-8">
                                                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                                                </TableCell>
                                            </TableRow>
                                        ) : trips.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                                    No trips found
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            trips.map((trip) => (
                                                <TableRow key={trip.tripId} className="hover:bg-muted/50">
                                                    <TableCell className="font-medium">#{trip.tripId}</TableCell>
                                                    <TableCell>{formatDate(trip.requestTime)}</TableCell>
                                                    <TableCell className="max-w-[150px] truncate" title={trip.pickupLocation}>
                                                        {trip.pickupLocation}
                                                    </TableCell>
                                                    <TableCell className="max-w-[150px] truncate" title={trip.dropoffLocation}>
                                                        {trip.dropoffLocation}
                                                    </TableCell>
                                                    <TableCell>{trip.driverName || 'N/A'}</TableCell>
                                                    <TableCell>{formatGlobalCurrency(trip.totalFare || trip.fare)}</TableCell>
                                                    <TableCell>{getTripStatusBadge(trip.status)}</TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                                {!isLoadingTrips && trips.length > 0 && (
                                    <DataTablePagination
                                        currentPage={currentPage + 1}
                                        totalPages={totalPages}
                                        totalItems={totalTrips}
                                        itemsPerPage={itemsPerPage}
                                        onPageChange={(page) => setCurrentPage(page - 1)}
                                        onItemsPerPageChange={(size) => {
                                            setItemsPerPage(size);
                                            setCurrentPage(0);
                                        }}
                                        className="mt-4"
                                    />
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* Export Tab */}
                <TabsContent value="export">
                    <div className="max-w-2xl mx-auto">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Download className="w-5 h-5 text-primary" />
                                    Export Rider Data
                                </CardTitle>
                                <CardDescription>
                                    Download rider information in various formats
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Export Format */}
                                <div className="space-y-3">
                                    <Label>Export Format</Label>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                        {(['CSV', 'PDF', 'JSON', 'XLSX'] as ExportFormat[]).map((format) => (
                                            <button
                                                key={format}
                                                onClick={() => setExportFormat(format)}
                                                className={`flex flex-col items-center justify-center p-4 rounded-lg border-2 transition-all ${
                                                    exportFormat === format
                                                        ? 'border-primary bg-primary/5'
                                                        : 'border-muted hover:border-primary/50'
                                                }`}
                                            >
                                                {format === 'CSV' && <FileSpreadsheet className="w-6 h-6 mb-2 text-green-600" />}
                                                {format === 'PDF' && <FileText className="w-6 h-6 mb-2 text-red-600" />}
                                                {format === 'JSON' && <FileJson className="w-6 h-6 mb-2 text-blue-600" />}
                                                {format === 'XLSX' && <FileSpreadsheet className="w-6 h-6 mb-2 text-green-700" />}
                                                <span className="font-medium">{format}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <Separator />

                                {/* Include Options */}
                                <div className="space-y-3">
                                    <Label>Include in Export</Label>
                                    <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="includeProfile"
                                                checked={includeProfile}
                                                onCheckedChange={(checked) => setIncludeProfile(checked as boolean)}
                                            />
                                            <Label htmlFor="includeProfile" className="cursor-pointer">
                                                Profile Information
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="includeTrips"
                                                checked={includeTrips}
                                                onCheckedChange={(checked) => setIncludeTrips(checked as boolean)}
                                            />
                                            <Label htmlFor="includeTrips" className="cursor-pointer">
                                                Trip History
                                            </Label>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <Checkbox
                                                id="includeStats"
                                                checked={includeStats}
                                                onCheckedChange={(checked) => setIncludeStats(checked as boolean)}
                                            />
                                            <Label htmlFor="includeStats" className="cursor-pointer">
                                                Statistics & Analytics
                                            </Label>
                                        </div>
                                    </div>
                                </div>

                                <Separator />

                                {/* Preview */}
                                <div className="bg-muted p-4 rounded-lg">
                                    <h4 className="font-medium mb-2">Export Preview</h4>
                                    <div className="text-sm text-muted-foreground space-y-1">
                                        <p>Rider: <span className="font-medium text-foreground">{rider.fullName}</span></p>
                                        <p>Format: <span className="font-medium text-foreground">{exportFormat}</span></p>
                                        <p>Includes:</p>
                                        <ul className="list-disc list-inside ml-2">
                                            {includeProfile && <li>Profile information</li>}
                                            {includeTrips && <li>Trip history ({rider.totalTrips || 0} trips)</li>}
                                            {includeStats && <li>Statistics and analytics</li>}
                                        </ul>
                                    </div>
                                </div>

                                {/* Export Button */}
                                <Button
                                    onClick={handleExport}
                                    disabled={isExporting || (!includeProfile && !includeTrips && !includeStats)}
                                    className="w-full"
                                >
                                    {isExporting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <Download className="w-4 h-4 mr-2" />
                                            Export Data
                                        </>
                                    )}
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default RiderProfilePage;
